import React from "react";
import cn from "./helpers/cn";
import Typography from "./Typography";
import { useFormControl, } from "./FormControl";

interface InputAdornmentProps extends React.HTMLAttributes<HTMLDivElement> {
  component?: React.ElementType;
  position: "start" | "end";
  disablePointerEvents?: boolean;
  disableTypography?: boolean;
  variant?: "standard" | "outlined" | "filled";
  children?: React.ReactNode;
}

const InputAdornment = React.forwardRef<HTMLDivElement, InputAdornmentProps>((props, ref) => {
  const {
    className,
    component: Component = "div",
    position,
    disablePointerEvents = false,
    disableTypography = false,
    variant: variantProp,
    children,
    ...other
  } = props;

  // Resolve variant: prop -> FormControl -> default "standard" (igual ao MUI v4).
  const muiFormControl = useFormControl();
  const variant = variantProp ?? muiFormControl?.variant ?? "standard";
  const hiddenLabel = !!muiFormControl?.hiddenLabel;

  // MUI v4 envolve children string em <Typography color="textSecondary"> a não ser
  // que disableTypography seja true.
  const wrappedChildren =
    typeof children === "string" && !disableTypography
      ? <Typography color="textSecondary">{children}</Typography>
      : children;

  return (
    <Component
      ref={ref}
      className={cn(
        // Base do MUI v4: flex, height 0.01em (fix flexbox no IE11), maxHeight 2em,
        // alinhamento central, sem quebra de linha.
        "flex items-center h-[0.01em] max-h-[2em] whitespace-nowrap text-slate-500",

        // Margem lateral conforme posição.
        position === "start" ? "mr-2" : "ml-2",

        // Filled + positionStart precisa de margem superior, exceto quando o label
        // está escondido (hiddenLabel) — mantém alinhamento com o input filled.
        variant === "filled" && position === "start" && !hiddenLabel && "mt-4",

        disablePointerEvents && "pointer-events-none",

        className
      )}
      {...other}
    >
      {wrappedChildren}
    </Component>
  );
});

InputAdornment.displayName = "InputAdornment";

export default InputAdornment;
